import { apiClient } from '@/api/client';
import {
  CHANNELS,
  channelByIdPath,
  channelMembersPath,
  channelMessagePath,
  channelMessagesPath,
} from '@/api/endpoints';

export type ChannelKind = 'standard' | 'announcement' | 'direct' | 'group_dm';
export type ChannelVisibility = 'public' | 'private';
export type ChannelGroupType =
  | 'organization'
  | 'company'
  | 'department'
  | 'branch'
  | 'team'
  | 'project'
  | 'custom';

export interface ChatUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  employment?: { department?: string };
}

export interface ChannelData {
  _id: string;
  organizationId: string;
  companyId?: string;
  name: string;
  description?: string;
  kind: ChannelKind;
  visibility: ChannelVisibility;
  groupType: ChannelGroupType;
  groupLabel?: string;
  groupValue?: string;
  isReadOnly: boolean;
  messagePermission: 'everyone' | 'owner_only';
  archivedAt?: string;
  archivedBy?: string;
  /** Present on archived channels from `GET /channels?archived=true`. */
  archivedByUser?: ChatUser | null;
  lastMessageSequence: number;
  lastMessageAt?: string;
  memberCount?: number;
  currentUserRole: 'owner' | 'moderator' | 'member' | null;
  createdByUser?: ChatUser | null;
  isCreatedByCurrentUser?: boolean;
  notificationLevel: 'all' | 'mentions' | 'muted';
  unreadCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChatAttachment {
  key: string;
  url: string;
  name: string;
  mimeType: string;
  size: number;
  /** Present for voice notes so duration is available before audio metadata loads. */
  durationSeconds?: number;
  /** Real amplitude samples captured while recording a voice note. */
  waveform?: number[];
}

export interface MessageReactionData {
  emoji: string;
  count: number;
  reacted: boolean;
}

export interface ChannelMessageData {
  _id: string;
  channelId: string;
  senderId: ChatUser | string;
  content: string;
  sequence: number;
  threadRootId?: string;
  replyCount: number;
  /** Whether the current user has saved this message (per viewer). */
  saved?: boolean;
  mentions: string[];
  /** Roles mentioned as a group (e.g. @manager). */
  mentionRoles?: string[];
  attachments: ChatAttachment[];
  reactions: MessageReactionData[];
  editedAt?: string;
  deletedAt?: string;
  clientMessageId?: string;
  /** Local-only state used while an optimistic message is being persisted. */
  deliveryState?: 'sending';
  createdAt: string;
  updatedAt: string;
}

export interface ChannelMemberData {
  _id: string;
  channelId: string;
  userId: ChatUser | string;
  role: 'owner' | 'moderator' | 'member';
  notificationLevel: 'all' | 'mentions' | 'muted';
}

export interface CreateChannelPayload {
  name: string;
  companyId?: string;
  description?: string;
  kind?: 'standard' | 'announcement';
  visibility?: ChannelVisibility;
  groupType?: ChannelGroupType;
  groupLabel?: string;
  groupValue?: string;
  memberIds?: string[];
  isReadOnly?: boolean;
  messagePermission?: 'everyone' | 'owner_only';
}

export interface CreateMessagePayload {
  content?: string;
  threadRootId?: string;
  mentions?: string[];
  /** Organization roles mentioned as a group, e.g. @manager. */
  mentionRoles?: string[];
  clientMessageId?: string;
  /** Duration captured locally for a single voice-note attachment. */
  voiceDurationSeconds?: number;
  /** Compressed real microphone amplitude samples for the voice-note attachment. */
  voiceWaveform?: number[];
}

function unwrap<T>(response: { data: T | { data: T } }): T {
  const payload = response.data;
  return payload && typeof payload === 'object' && 'data' in payload
    ? (payload as { data: T }).data
    : (payload as T);
}

export async function getChannels(archived = false): Promise<ChannelData[]> {
  const data = unwrap<ChannelData[]>(
    await apiClient.get(CHANNELS, { params: archived ? { archived: 'true' } : undefined })
  );
  return Array.isArray(data) ? data : [];
}

export async function getChannel(channelId: string): Promise<ChannelData> {
  return unwrap<ChannelData>(await apiClient.get(channelByIdPath(channelId)));
}

export async function createChannel(payload: CreateChannelPayload): Promise<ChannelData> {
  return unwrap<ChannelData>(await apiClient.post(CHANNELS, payload));
}

export async function createDirectChannel(userIds: string[], name?: string): Promise<ChannelData> {
  return unwrap<ChannelData>(
    await apiClient.post(`${CHANNELS}/direct`, {
      userIds,
      ...(name?.trim() ? { name: name.trim() } : {}),
    })
  );
}

export async function updateChannel(
  channelId: string,
  payload: Partial<
    Pick<ChannelData, 'name' | 'description' | 'visibility' | 'isReadOnly' | 'messagePermission'>
  > & {
    archived?: boolean;
  }
): Promise<ChannelData> {
  return unwrap<ChannelData>(await apiClient.patch(channelByIdPath(channelId), payload));
}

/** Owner only; the API rejects direct and group conversations. */
export async function archiveChannel(channelId: string): Promise<void> {
  await apiClient.post(`${channelByIdPath(channelId)}/archive`);
}

/** Owner only; members who left the channel's company stay out. */
export async function restoreChannel(channelId: string): Promise<ChannelData> {
  return unwrap<ChannelData>(await apiClient.post(`${channelByIdPath(channelId)}/restore`));
}

/** An organization role that can be @mentioned in a channel. */
export interface MentionGroup {
  id: string;
  name: string;
  slug: string;
  /** How many members of this channel hold the role. */
  memberCount: number;
}

export async function getMentionGroups(channelId: string): Promise<MentionGroup[]> {
  const data = unwrap<MentionGroup[]>(
    await apiClient.get(`${channelByIdPath(channelId)}/mention-groups`)
  );
  return Array.isArray(data) ? data : [];
}

/** `idle` = tab open but inactive; shown as "Away". */
export type PresenceStatus = 'online' | 'idle' | 'offline';

export interface ChatPresence {
  userId: string;
  status: PresenceStatus;
}

/** People the caller may not see come back as offline. */
export async function getChatPresence(userIds: string[]): Promise<ChatPresence[]> {
  if (userIds.length === 0) return [];
  const data = unwrap<ChatPresence[]>(
    await apiClient.get(`${CHANNELS}/presence`, { params: { userIds: userIds.join(',') } })
  );
  return Array.isArray(data) ? data : [];
}

export async function getChatPeople(query?: string): Promise<ChatUser[]> {
  const data = unwrap<ChatUser[]>(
    await apiClient.get(`${CHANNELS}/people`, { params: query ? { q: query } : undefined })
  );
  return Array.isArray(data) ? data : [];
}

export async function getChannelCreationMembers(companyId: string): Promise<ChatUser[]> {
  const data = unwrap<ChatUser[]>(
    await apiClient.get(`${CHANNELS}/creation/members`, { params: { companyId } })
  );
  return Array.isArray(data) ? data : [];
}

/** Departments and teams (manager + direct reports) in a company, for audience channels. */
export interface ChannelAudiences {
  departments: Array<{ name: string; memberCount: number }>;
  teams: Array<{ managerId: string; managerName: string; memberCount: number }>;
}

export async function getChannelAudiences(companyId: string): Promise<ChannelAudiences> {
  const data = unwrap<ChannelAudiences>(
    await apiClient.get(`${CHANNELS}/creation/audiences`, { params: { companyId } })
  );
  return {
    departments: Array.isArray(data?.departments) ? data.departments : [],
    teams: Array.isArray(data?.teams) ? data.teams : [],
  };
}

export async function getChannelMembers(channelId: string): Promise<ChannelMemberData[]> {
  const data = unwrap<ChannelMemberData[]>(await apiClient.get(channelMembersPath(channelId)));
  return Array.isArray(data) ? data : [];
}

export async function getEligibleChannelMembers(channelId: string): Promise<ChatUser[]> {
  const data = unwrap<ChatUser[]>(
    await apiClient.get(`${channelByIdPath(channelId)}/eligible-members`)
  );
  return Array.isArray(data) ? data : [];
}

export async function addChannelMember(
  channelId: string,
  userId: string,
  role: 'moderator' | 'member' = 'member'
): Promise<ChannelMemberData> {
  return unwrap<ChannelMemberData>(
    await apiClient.post(channelMembersPath(channelId), { userId, role })
  );
}

export async function updateChannelMember(
  channelId: string,
  memberId: string,
  payload: { role?: 'moderator' | 'member'; notificationLevel?: 'all' | 'mentions' | 'muted' }
): Promise<ChannelMemberData> {
  return unwrap<ChannelMemberData>(
    await apiClient.patch(`${channelMembersPath(channelId)}/${memberId}`, payload)
  );
}

export async function removeChannelMember(channelId: string, memberId: string): Promise<void> {
  await apiClient.delete(`${channelMembersPath(channelId)}/${memberId}`);
}

export async function getChannelMessages(
  channelId: string,
  options?: { before?: number; around?: number; threadRootId?: string }
): Promise<ChannelMessageData[]> {
  const data = unwrap<ChannelMessageData[]>(
    await apiClient.get(channelMessagesPath(channelId), { params: options })
  );
  return Array.isArray(data) ? data : [];
}

export async function createChannelMessage(
  channelId: string,
  payload: CreateMessagePayload
): Promise<ChannelMessageData> {
  return unwrap<ChannelMessageData>(await apiClient.post(channelMessagesPath(channelId), payload));
}

export async function createChannelMessageWithFiles(
  channelId: string,
  payload: CreateMessagePayload,
  files: File[]
): Promise<ChannelMessageData> {
  const formData = new FormData();
  if (payload.content) formData.append('content', payload.content);
  if (payload.threadRootId) formData.append('threadRootId', payload.threadRootId);
  if (payload.clientMessageId) formData.append('clientMessageId', payload.clientMessageId);
  if (payload.voiceDurationSeconds !== undefined) {
    formData.append('voiceDurationSeconds', String(payload.voiceDurationSeconds));
  }
  if (payload.voiceWaveform?.length) {
    formData.append('voiceWaveform', JSON.stringify(payload.voiceWaveform));
  }
  // Arrays travel as JSON strings in multipart; the API parses them back.
  if (payload.mentions?.length) formData.append('mentions', JSON.stringify(payload.mentions));
  if (payload.mentionRoles?.length) {
    formData.append('mentionRoles', JSON.stringify(payload.mentionRoles));
  }
  files.forEach((file) => formData.append('files', file));
  return unwrap<ChannelMessageData>(
    await apiClient.post(`${channelMessagesPath(channelId)}/attachments`, formData)
  );
}

export async function updateChannelMessage(
  channelId: string,
  messageId: string,
  payload: Pick<CreateMessagePayload, 'content' | 'mentions' | 'mentionRoles'>
): Promise<ChannelMessageData> {
  return unwrap<ChannelMessageData>(
    await apiClient.put(channelMessagePath(channelId, messageId), payload)
  );
}

export async function voteChannelPoll(
  channelId: string,
  messageId: string,
  optionId: string
): Promise<ChannelMessageData> {
  return unwrap<ChannelMessageData>(
    await apiClient.post(`${channelMessagePath(channelId, messageId)}/vote`, { optionId })
  );
}

export async function deleteChannelMessage(channelId: string, messageId: string): Promise<void> {
  await apiClient.delete(channelMessagePath(channelId, messageId));
}

export async function toggleMessageReaction(
  channelId: string,
  messageId: string,
  emoji: string
): Promise<{ reactions: MessageReactionData[] }> {
  return unwrap<{ reactions: MessageReactionData[] }>(
    await apiClient.post(`${channelMessagePath(channelId, messageId)}/reactions`, { emoji })
  );
}

export async function toggleMessagePin(
  channelId: string,
  messageId: string
): Promise<{ pinned: boolean }> {
  return unwrap<{ pinned: boolean }>(
    await apiClient.post(`${channelMessagePath(channelId, messageId)}/pin`)
  );
}

export async function getPinnedMessages(channelId: string): Promise<ChannelMessageData[]> {
  const data = unwrap<ChannelMessageData[]>(
    await apiClient.get(`${channelByIdPath(channelId)}/pins`)
  );
  return Array.isArray(data) ? data : [];
}

export interface SavedMessage extends MessageSearchResult {
  savedAt: string;
}

export interface SavedMessagesPage {
  items: SavedMessage[];
  /** Pass back as `before` for older saves; null when there are no more. */
  nextCursor: string | null;
}

export async function getSavedMessages(before?: string): Promise<SavedMessagesPage> {
  const data = unwrap<SavedMessagesPage>(
    await apiClient.get(`${CHANNELS}/saved`, { params: before ? { before } : undefined })
  );
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    nextCursor: data?.nextCursor ?? null,
  };
}

export async function toggleSavedMessage(
  channelId: string,
  messageId: string
): Promise<{ saved: boolean }> {
  return unwrap<{ saved: boolean }>(
    await apiClient.post(`${channelMessagePath(channelId, messageId)}/save`)
  );
}

export async function markChannelRead(
  channelId: string,
  sequence?: number
): Promise<{ lastReadSequence: number }> {
  return unwrap<{ lastReadSequence: number }>(
    await apiClient.post(`${channelByIdPath(channelId)}/read`, sequence == null ? {} : { sequence })
  );
}

export type MessageSearchScope = 'all' | 'channels' | 'direct' | 'threads';

export interface MessageSearchParams {
  q: string;
  channelId?: string;
  scope?: MessageSearchScope;
  senderId?: string;
  /** ISO date-time, inclusive. */
  from?: string;
  /** ISO date-time, inclusive. */
  to?: string;
  hasAttachments?: boolean;
}

export interface MessageSearchResult extends ChannelMessageData {
  /** Present on thread replies so the thread can open without another request. */
  threadRoot?: ChannelMessageData | null;
}

export async function searchChannelMessages(
  params: MessageSearchParams
): Promise<MessageSearchResult[]> {
  const { scope, hasAttachments, ...rest } = params;
  const data = unwrap<MessageSearchResult[]>(
    await apiClient.get(`${CHANNELS}/search/messages`, {
      params: {
        ...rest,
        ...(scope && scope !== 'all' ? { scope } : {}),
        ...(hasAttachments ? { hasAttachments: true } : {}),
      },
    })
  );
  return Array.isArray(data) ? data : [];
}
